import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from 'store/store';
import { getRequest } from 'api/apiClient';
import { OpModal } from 'components/customAntd/DLS/OpModal/OpModal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Empty, List, Spin } from 'antd';
import { LoadingOutlined, EyeOutlined } from '@ant-design/icons';
import { GuestPass, GuestPassLog } from 'types/guestPassTypes';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { Store } from 'antd/es/form/interface';
import { OpRow } from 'components/customAntd/DLS/OpRow/OpRow';
import { OpCol } from 'components/customAntd/DLS/OpCol/OpCol';
import { formatFullName } from 'utils/utils';
import { getStatusColor, getStatusNameById } from 'utils/visitorsHelper';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { DATE_TIME_AM_PM_FORMAT, DATE_TIME_FORMAT } from 'constants/dates';
import { useTranslation } from 'react-i18next';
import { fetchMsiAltaEntries } from 'store/slices/integrationSlice';

dayjs.extend(utc);

const GuestPassLogTab: React.FC = () => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const { selectedVisit } = useSelector((state: RootState) => state.visits);
    const triggerUpdate = useSelector((state: RootState) => state.guestPass.triggerUpdate);
    const msiAltaEntries = useSelector((state: RootState) => state.integration.msiAltaEntries);
    const entriesLoaded = useSelector((state: RootState) => state.integration.entriesLoaded);

    const [guestPasses, setGuestPasses] = useState<GuestPass[]>([]);
    const [guestPassLogs, setGuestPassLogs] = useState<Record<number, GuestPassLog[]>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedLog, setSelectedLog] = useState<GuestPassLog | null>(null);

    const { t } = useTranslation();

    // Fetch MSI Alta entries if not already loaded
    useEffect(() => {
        if (orgId && !entriesLoaded) {
            dispatch(fetchMsiAltaEntries({ orgId }));
        }
    }, [orgId, entriesLoaded, dispatch]);

    useEffect(() => {
        const fetchLogs = async () => {
            if (!entriesLoaded || !selectedVisit) return;

            setLoading(true);
            try {
                let baseStartDate: string;
                if (selectedVisit.scheduleStart == null) {
                    baseStartDate = selectedVisit.signIn!;
                } else {
                    baseStartDate = selectedVisit.scheduleStart;
                }
                const startDate = dayjs(baseStartDate).subtract(7, 'days').format('YYYY-MM-DD');

                let baseEndDate: string;
                if (selectedVisit.scheduleEnd == null) {
                    if (selectedVisit.signOut == null) {
                        baseEndDate = selectedVisit.signIn!;
                    } else {
                        baseEndDate = selectedVisit.signOut;
                    }
                } else {
                    baseEndDate = selectedVisit.scheduleEnd;
                }
                const endDate = dayjs(baseEndDate).add(7, 'days').format('YYYY-MM-DD');

                // Convert to UTC (matching Android's convertLocalToUTC)
                const startUtc = dayjs(`${startDate} 00:00:00`).utc().format(DATE_TIME_FORMAT);
                const endUtc = dayjs(`${endDate} 23:59:59`).utc().format(DATE_TIME_FORMAT);

                // Fetch guest passes
                const guestPassResponse = await getRequest(`/orgs/${orgId}/msiAltaGuestPassByDate`, {
                    startDate: startUtc,
                    endDate: endUtc,
                    visitId: selectedVisit.id,
                });
                const fetchedGuestPasses = guestPassResponse.data;
                setGuestPasses(fetchedGuestPasses);

                // Fetch logs for each guest pass
                const logsByGuestPass: Record<number, GuestPassLog[]> = {};
                for (const pass of fetchedGuestPasses) {
                    const logResponse = await getRequest(`/orgs/${orgId}/visitor/${selectedVisit.id}/msiAltaGuestPass/${pass.id}/log`, {
                        startUtc,
                        endUtc,
                    });
                    logsByGuestPass[pass.id] = logResponse.data.sort((a: GuestPassLog, b: GuestPassLog) =>
                        dayjs(b.requestedUtc).diff(dayjs(a.requestedUtc))
                    );
                }
                setGuestPassLogs(logsByGuestPass);
            } catch (error) {
                console.error('Failed to fetch logs:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchLogs();
        // eslint-disable-next-line
    }, [orgId, selectedVisit?.id, triggerUpdate, selectedVisit?.scheduleEnd, selectedVisit?.scheduleStart, selectedVisit?.signIn, selectedVisit?.signOut, entriesLoaded]);

    const isGuestPassValid = (guestPass: GuestPass): boolean => {
        if (!guestPass?.startUtc || !guestPass?.endUtc) return false;
        const now = dayjs.utc();
        const start = dayjs.utc(guestPass.startUtc);
        const end = dayjs.utc(guestPass.endUtc);
        return now.isAfter(start) && now.isBefore(end) && guestPass.status === 1;
    };

    const getEntryName = (entryId: number): string => {
        const entry = msiAltaEntries.find((entry) => entry.value === entryId);
        return entry ? entry.label : t('visitors.guestPassLog.unknownEntry');
    };

    return (
        <>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Spin indicator={<LoadingOutlined spin />} />
                </div>
            ) : guestPasses.length === 0 ? ( // Check if guestPasses is empty
                <Empty description={t('visitors.guestPassLog.noData')} />
            ) : (
                <OpSpace
                    direction="vertical"
                    size="middle"
                    style={{
                        display: 'flex',
                    }}
                >
                    {guestPasses
                        .sort((a, b) => {
                            const validA = isGuestPassValid(a);
                            const validB = isGuestPassValid(b);
                            if (validA === validB) {
                                return dayjs(b.startUtc).diff(dayjs(a.startUtc)); // Sort by start date if both valid/invalid
                            }
                            return validA ? -1 : 1; // Valid passes first
                        })
                        .map((guestPass) => (
                            <div key={guestPass.id}>
                                <List
                                    header={
                                        <>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span>
                                                    {t('visitors.guestPassLog.issued')} : {dayjs(guestPass.createdAt).utc(true).local().format(DATE_TIME_AM_PM_FORMAT)}
                                                </span>
                                                <span
                                                    style={{
                                                        marginLeft: '16px',
                                                        fontWeight: 'bold',
                                                        color: isGuestPassValid(guestPass) ? 'green' : 'var(--colorTextDisabled)',
                                                    }}
                                                >
                                                    {isGuestPassValid(guestPass) ? t('visitors.guestPassLog.valid') : t('visitors.guestPassLog.invalid')}
                                                </span>
                                            </div>
                                            {guestPass.generatedBy?.[0]?.firstName || guestPass.generatedBy?.[0]?.lastName ? (
                                                <div>
                                                    <span>{t('visitors.guestPassLog.issuedBy')} : </span>
                                                    {formatFullName(guestPass.generatedBy?.[0].firstName, null, guestPass.generatedBy?.[0].lastName)}
                                                </div>
                                            ) : null}
                                        </>
                                    }
                                    dataSource={guestPassLogs[guestPass.id] || []}
                                    bordered
                                    renderItem={(log) => (
                                        <List.Item
                                            onClick={() => setSelectedLog(log)}
                                            style={{ cursor: 'pointer' }}
                                            actions={[
                                                <EyeOutlined
                                                    key="view"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedLog(log);
                                                    }}
                                                    style={{ fontSize: '16px', cursor: 'pointer' }}
                                                />
                                            ]}
                                        >
                                            <div style={{ flex: 1 }}>{dayjs(log.requestedUtc).utc(true).local().format(DATE_TIME_AM_PM_FORMAT)}</div>
                                            <div style={{ flex: 1 }}>{getEntryName(log.entryId)}</div>
                                            <div
                                                style={{
                                                    flex: 1,
                                                    color: log.success ? 'green' : 'var(--colorError)',
                                                }}
                                            >
                                                {log.success ? t('visitors.guestPassLog.success') : t('visitors.guestPassLog.failed')}
                                            </div>
                                        </List.Item>
                                    )}
                                />
                            </div>
                        ))}
                </OpSpace>
            )}
            {selectedLog && (
                <OpModal
                    title={t('visitors.guestPassLog.title')}
                    open={!!selectedLog}
                    onCancel={() => setSelectedLog(null)}
                    footer={null}
                >
                    <OpForm
                        initialValues={{
                            visitor: formatFullName(selectedVisit?.visitors[0]?.firstName || null, selectedVisit?.visitors[0]?.middleName || null, selectedVisit?.visitors[0]?.lastName || null)
                                + (selectedVisit?.visitors.length! > 1 ? ` (+${selectedVisit?.visitors.length! - 1})` : ''),
                            status: getStatusNameById(selectedVisit?.visitStatus.id!, selectedVisit?.scheduleStart),
                            requested: dayjs(selectedLog.requestedUtc).utc(true).local().format('YYYY-MM-DD h:mm A'),
                            entry: getEntryName(selectedLog.entryId),
                            ipAddress: selectedLog.ipAddress,
                            result: selectedLog.success ? t('visitors.guestPassLog.success') : t('visitors.guestPassLog.failed'),
                        }}
                        defaultButtons={false}
                        hasError={false}
                        onSubmit={function (args: IOnSubmitArgs<Store>): void {
                            throw new Error('Function not implemented.');
                        }}
                    >
                        <OpRow gutter={16}>
                            <OpCol span={12}>
                                <OpForm.Input label={t('visitors.guestPassLog.visitor')} name="visitor" readOnly />
                            </OpCol>
                            <OpCol span={12}>
                                <OpForm.Input
                                    label={t('visitors.guestPassLog.status')}
                                    name="status"
                                    readOnly
                                    style={{
                                        backgroundColor: getStatusColor(getStatusNameById(selectedVisit?.visitStatus.id!, selectedVisit?.scheduleStart)),
                                        color: 'white',
                                    }}
                                />
                            </OpCol>
                        </OpRow>
                        <OpRow gutter={16}>
                            <OpCol span={12}>
                                <OpForm.Input label={t('visitors.guestPassLog.requested')} name="requested" readOnly />
                            </OpCol>
                            <OpCol span={12}>
                                <OpForm.Input label={t('visitors.guestPassLog.entry')} name="entry" readOnly />
                            </OpCol>
                        </OpRow>
                        <OpRow gutter={16}>
                            <OpCol span={12}>
                                <OpForm.Input label={t('visitors.guestPassLog.ipAddress')} name="ipAddress" readOnly />
                            </OpCol>
                            <OpCol span={12}>
                                <OpForm.Input
                                    label={t('visitors.guestPassLog.result')}
                                    name="result"
                                    readOnly
                                    style={{
                                        color: selectedLog.success ? 'green' : 'var(--colorError)',
                                    }}
                                />
                            </OpCol>
                        </OpRow>
                    </OpForm>
                </OpModal>
            )}
        </>
    );
};

export default GuestPassLogTab;
